import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Form, Input, Button, message, Select, Row, Col, Switch, Card, Divider } from 'antd-v4'
import { LinkOutlined, KeyOutlined } from '@ant-design/icons'
import { IAdminExternalSource, ILocation } from '@getgreenline/homi-shared'
import { parseErrorMsg } from '../../../utilities/helpers'
import { observer } from 'mobx-react-lite'
import { inject } from 'mobx-react'
import { ISubscription, ISubscriptionContract } from '@getgreenline/webhooks'
import { GreenlineEventTypes } from '@getgreenline/events'
import { buildSubscriptionPayload, isSubscriptionActive, sortByName } from './subscriptionHelpers'

interface Props {
  externalSources: IAdminExternalSource[]
  locations: ILocation[]
  onSubmit: (subscription: ISubscription) => Promise<ISubscriptionContract | void>
  companyId: number | undefined
  existingSubscription?: ISubscriptionContract
}

interface FormValues {
  eventType: string
  locationId?: number
  integratorId: string
  url: string
  integratorSecret?: string
  isActive?: boolean
}

const SubscriptionSourceForm = inject('currentCompanyStore')(
  observer((props: Props) => {
    const [form] = Form.useForm<FormValues>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isEdit = !!props.existingSubscription

    // A successful edit closes the drawer this form lives in (parent unmounts it),
    // so guard the loading-state reset — an unmounted component can't setState.
    const isMountedRef = useRef(true)
    useEffect(
      () => () => {
        isMountedRef.current = false
      },
      [],
    )

    const eventTypes = sortByName(Object.values(GreenlineEventTypes), (nameKey) => nameKey)
    const sortedLocations = sortByName(props.locations, (location) => location.name)
    const sortedExternalSources = sortByName(props.externalSources, (source) => source.name)

    const initialValues: Partial<FormValues> = {
      eventType: props.existingSubscription?.companyData.eventType,
      locationId: props.existingSubscription?.companyData.locationId,
      integratorId: props.existingSubscription?.companyData.integratorId,
      url: props.existingSubscription?.companyData.url,
      integratorSecret: props.existingSubscription?.companyData.integratorSecret,
      isActive: props.existingSubscription
        ? isSubscriptionActive(props.existingSubscription)
        : true,
    }

    const handleSubmit = async (values: FormValues) => {
      setIsSubmitting(true)
      try {
        if (!props.companyId) throw Error('Company not found')

        await props.onSubmit(
          buildSubscriptionPayload({
            companyId: props.companyId,
            eventType: values.eventType,
            integratorId: values.integratorId,
            url: values.url,
            locationId: values.locationId,
            integratorSecret: values.integratorSecret,
            isActive: values.isActive,
            isEdit,
          }),
        )
        form.resetFields()
      } catch (error) {
        message.error(parseErrorMsg(error) || 'An error occurred while creating a subscription')
      } finally {
        if (isMountedRef.current) setIsSubmitting(false)
      }
    }

    const formBody = (
      <Form form={form} layout='vertical' onFinish={handleSubmit} initialValues={initialValues}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name='eventType' label='Event type' rules={[{ required: true }]}>
              <Select
                placeholder='Select an option'
                allowClear
                showSearch
                optionFilterProp='children'
              >
                {eventTypes.map((nameKey: string) => (
                  <Select.Option key={nameKey} value={nameKey}>
                    {nameKey}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name='locationId' label='Location'>
              {/* getSubscriptionUpdateObject (greenline-webhook-payload-builder/lambda/common/
                  helpers.ts:87-104) falls back to the existing stored location whenever the
                  incoming value is falsy, so clearing this field on edit is a no-op. Only allow
                  clearing on create, where an omitted locationId genuinely means "no location". */}
              <Select
                placeholder='Select an option'
                allowClear={!isEdit}
                showSearch
                optionFilterProp='children'
              >
                {sortedLocations.map((location) => (
                  <Select.Option key={location.id} value={location.id}>
                    {location.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name='integratorId' label='Integrator' rules={[{ required: true }]}>
              <Select
                placeholder='Select an option'
                allowClear
                showSearch
                optionFilterProp='children'
              >
                {sortedExternalSources.map((source) => (
                  <Select.Option key={source.id} value={source.id}>
                    {source.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='url'
              label='URL'
              rules={[{ required: true, message: 'Please input a webhook URL' }]}
            >
              <Input
                prefix={<LinkOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder='https://example.com/webhooks'
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name='integratorSecret'
              label='Integrator secret'
              extra={
                isEdit
                  ? 'Leave unchanged to keep the current secret. Clearing this field does not remove it.'
                  : undefined
              }
            >
              <Input
                prefix={<KeyOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder='IntegratorSecret'
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider className='mt-2 mb-3' />

        <div className='d-flex justify-content-between align-items-center'>
          {isEdit ? (
            <Form.Item name='isActive' valuePropName='checked' className='mb-0'>
              <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
            </Form.Item>
          ) : (
            <span />
          )}

          <Button type='primary' htmlType='submit' loading={isSubmitting}>
            {isEdit ? 'Save changes' : 'Create subscription'}
          </Button>
        </div>
      </Form>
    )

    if (isEdit) {
      return formBody
    }

    return (
      <Card title='Create subscription' className='mb-4'>
        <p className='text-muted'>
          Events matching the selected type are delivered to your endpoint.
        </p>
        {formBody}
      </Card>
    )
  }),
)

export default SubscriptionSourceForm
